import { Link } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { EditModal, SummaryCard } from '@nucleus-commerce/admin-kit/forms';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type {
  Product,
  PutStoneSettingRequest,
  StoneSetting,
} from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { toast } from '@nucleus-commerce/ui/components/sonner';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import {
  useConfigurator,
  useRemoveConfigurator,
  useSetConfigurator,
} from '../hooks/use-stone-setting';

import { CardEditAction, SharedChip } from './product-scope';

type Shape = PutStoneSettingRequest['acceptedShapes'][number];
type StoneType = PutStoneSettingRequest['acceptedStoneTypes'][number];

const SHAPES: Shape[] = [
  'round',
  'oval',
  'emerald',
  'cushion',
  'pear',
  'marquise',
  'princess',
  'radiant',
  'asscher',
  'heart',
  'rose',
];
const STONE_TYPES: StoneType[] = ['lab_diamond', 'natural_diamond', 'gemstone'];

function toggle<T>(set: ReadonlySet<T>, value: T): Set<T> {
  const next = new Set(set);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

/** Stones the setting would take that shoppers can't see because no markup band prices them. */
function hasHiddenStones(setting: StoneSetting) {
  return setting.stonePriceRuleCount === 0 || setting.unpricedStoneCount > 0;
}

export interface ConfiguratorSectionProps {
  product: Product;
}

/** Whether shoppers choose the center diamond. It saves on its own, not with the page. */
export function ConfiguratorSection({ product }: ConfiguratorSectionProps) {
  const { t } = useTranslation('products');
  const { can } = usePermissions();
  const query = useConfigurator(product.id);
  const [editing, setEditing] = useState(false);

  let body: ReactNode;
  if (query.isPending) body = <Skeleton className="h-5 w-56" />;
  else if (query.error) {
    body = <ErrorState error={query.error} onRetry={() => void query.refetch()} compact />;
  } else body = <DiamondSummary setting={query.data ?? null} />;

  return (
    <SummaryCard
      title={t('configurator.title')}
      badge={<SharedChip />}
      action={
        query.isSuccess ? (
          <CardEditAction onEdit={() => setEditing(true)} disabled={!can('configurator.write')} />
        ) : null
      }
    >
      {body}
      {editing ? (
        <DiamondModal
          productId={product.id}
          setting={query.data ?? null}
          onClose={() => setEditing(false)}
        />
      ) : null}
    </SummaryCard>
  );
}

function DiamondSummary({ setting }: { setting: StoneSetting | null }) {
  const { t } = useTranslation('products');
  if (!setting) return <span>{t('diamondCard.off')}</span>;
  const { caratMin: min, caratMax: max } = setting;
  const carat =
    min != null && max != null
      ? t('diamondCard.caratRange', { min, max })
      : min != null
        ? t('diamondCard.caratFrom', { min })
        : max != null
          ? t('diamondCard.caratUpTo', { max })
          : null;
  const shapes = SHAPES.filter((shape) => setting.acceptedShapes.includes(shape)).map((shape) =>
    t(`configurator.shape.${shape}`),
  );
  return (
    <>
      <span>{[t('diamondCard.on'), shapes.join(', '), carat].filter(Boolean).join(' · ')}</span>
      {hasHiddenStones(setting) ? (
        <span className="bg-destructive/10 text-destructive w-fit rounded-md px-1.5 py-0.5 text-xs">
          {setting.stonePriceRuleCount === 0
            ? t('diamondCard.noBands')
            : t('diamondCard.hidden', { count: setting.unpricedStoneCount })}
        </span>
      ) : null}
    </>
  );
}

function Choice({
  checked,
  onToggle,
  children,
}: {
  checked: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <label
      className={cn(
        'flex cursor-pointer items-center gap-2 rounded-lg border px-2.5 py-1.5 text-[13px] font-medium',
        checked && 'border-primary/50 bg-primary-tint',
      )}
    >
      <Checkbox checked={checked} onCheckedChange={onToggle} />
      {children}
    </label>
  );
}

function CaratInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation('products');
  const id = useId();
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          id={id}
          type="number"
          min={0}
          max={100}
          step="0.01"
          value={value}
          placeholder={t('configurator.noLimit')}
          className="pr-9"
          onChange={(e) => onChange(e.target.value)}
        />
        <span className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-[13px]">
          {t('diamondCard.caratUnit')}
        </span>
      </div>
    </div>
  );
}

/** An action modal: Save writes at once, because this setting has its own endpoint. */
function DiamondModal({
  productId,
  setting,
  onClose,
}: {
  productId: string;
  setting: StoneSetting | null;
  onClose: () => void;
}) {
  const { t } = useTranslation(['products', 'common']);
  const setConfigurator = useSetConfigurator(productId);
  const removeConfigurator = useRemoveConfigurator(productId);
  const saving = setConfigurator.isPending || removeConfigurator.isPending;
  const message = useErrorMessage();
  const failed = setConfigurator.error ?? removeConfigurator.error;
  // Everything else is toasted globally; a validation failure is left to the form.
  const rejected = isApiError(failed) && failed.kind === 'validation';

  const [enabled, setEnabled] = useState(setting !== null);
  const [shapes, setShapes] = useState<Set<Shape>>(new Set(setting?.acceptedShapes ?? []));
  const [stoneTypes, setStoneTypes] = useState<Set<StoneType>>(
    new Set(setting?.acceptedStoneTypes ?? ['lab_diamond', 'natural_diamond']),
  );
  const [caratMin, setCaratMin] = useState(
    setting?.caratMin != null ? String(setting.caratMin) : '',
  );
  const [caratMax, setCaratMax] = useState(
    setting?.caratMax != null ? String(setting.caratMax) : '',
  );

  const min = caratMin === '' ? null : Number(caratMin);
  const max = caratMax === '' ? null : Number(caratMax);
  const emptyChoice = shapes.size === 0 || stoneTypes.size === 0;
  const caratsCrossed = min !== null && max !== null && min > max;
  const invalid = enabled && (emptyChoice || caratsCrossed);

  const save = () => {
    if (enabled) {
      setConfigurator.mutate(
        {
          acceptedShapes: [...shapes],
          acceptedStoneTypes: [...stoneTypes],
          caratMin: min,
          caratMax: max,
        },
        {
          onSuccess: () => {
            toast.success(t('products:configurator.toasts.saved'));
            onClose();
          },
        },
      );
    } else if (setting) {
      removeConfigurator.mutate(undefined, {
        onSuccess: () => {
          toast.success(t('products:configurator.toasts.removed'));
          onClose();
        },
      });
    } else onClose();
  };

  return (
    <EditModal
      open
      onOpenChange={(open) => !open && onClose()}
      size="lg"
      title={t('products:configurator.title')}
      description={t('products:diamondCard.description')}
      footerStart={
        invalid ? (
          <span className="text-muted-foreground text-xs">
            {emptyChoice
              ? t('products:configurator.pickAtLeastOne')
              : t('products:diamondCard.caratsCrossed')}
          </span>
        ) : rejected ? (
          <span role="alert" className="text-destructive text-xs">
            {message(failed)}
          </span>
        ) : null
      }
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="button" disabled={invalid || saving} onClick={save}>
            {t('common:actions.save')}
          </Button>
        </>
      }
    >
      <label className="flex items-start gap-3">
        <Switch className="mt-0.5" checked={enabled} onCheckedChange={setEnabled} />
        <span className="text-sm font-medium">{t('products:diamondCard.toggle')}</span>
      </label>
      {enabled ? (
        <>
          {setting && hasHiddenStones(setting) ? (
            <Alert variant="destructive" role="status">
              <AlertTriangle aria-hidden />
              <AlertTitle>
                {setting.stonePriceRuleCount === 0
                  ? t('products:configurator.pricing.noBands')
                  : t('products:configurator.pricing.unpriced', {
                      count: setting.unpricedStoneCount,
                    })}
              </AlertTitle>
              <AlertDescription>
                <span>{t('products:configurator.pricing.hint')}</span>
                <Link to="/org/stone-pricing" className="font-medium underline">
                  {t('products:configurator.pricing.open')}
                </Link>
              </AlertDescription>
            </Alert>
          ) : null}
          <fieldset className="flex flex-col gap-2.5">
            <legend className="mb-2.5 text-sm font-semibold">
              {t('products:configurator.shapes')}
            </legend>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
              {SHAPES.map((shape) => (
                <Choice
                  key={shape}
                  checked={shapes.has(shape)}
                  onToggle={() => setShapes((prev) => toggle(prev, shape))}
                >
                  {t(`products:configurator.shape.${shape}`)}
                </Choice>
              ))}
            </div>
          </fieldset>
          <fieldset className="flex flex-col gap-2.5">
            <legend className="mb-2.5 text-sm font-semibold">
              {t('products:configurator.stoneTypes')}
            </legend>
            <div className="grid gap-2 sm:grid-cols-3">
              {STONE_TYPES.map((type) => (
                <Choice
                  key={type}
                  checked={stoneTypes.has(type)}
                  onToggle={() => setStoneTypes((prev) => toggle(prev, type))}
                >
                  {t(`products:configurator.stoneType.${type}`)}
                </Choice>
              ))}
            </div>
          </fieldset>
          <div className="flex flex-col gap-2.5">
            <h3 className="text-sm font-semibold">{t('products:diamondCard.caratRangeTitle')}</h3>
            <div className="grid grid-cols-2 gap-3">
              <CaratInput
                label={t('products:diamondCard.caratMin')}
                value={caratMin}
                onChange={setCaratMin}
              />
              <CaratInput
                label={t('products:diamondCard.caratMax')}
                value={caratMax}
                onChange={setCaratMax}
              />
            </div>
          </div>
        </>
      ) : null}
    </EditModal>
  );
}
